<template>
  <div class="flex flex-col md:flex-row gap-5">
    <div class="flex flex-col gap-2 w-full md:w-1/3">
      <div v-if="allocatedInspections && allocatedInspections > 0" class="flex flex-col items-center self-center">
        <Knob readonly v-model="inspectionsThisDraw" :max="allocatedInspections" :size="150" />
        <p>Of {{ allocatedInspections }} Inspections</p>
        <div v-if="canEdit" class="flex gap-2">
          <Button icon="pi pi-plus" @click="inspectionsThisDraw++" :disabled="inspectionsThisDraw >= allocatedInspections" />
          <Button icon="pi pi-minus" @click="inspectionsThisDraw--" :disabled="inspectionsThisDraw <= 0" />
        </div>
      </div>
      <InputGroup v-else class="w-full h-10">
        <InputGroupAddon>Insp. This Draw</InputGroupAddon>
        <InputNumber v-model="inspectionsThisDraw" input-class="w-32!" />
      </InputGroup>
      <Button label="Request Inspection" :disabled="!canEdit" class="h-10 w-full" @click="requestInspectionClick" />
      <Button label="Import Inspection" :disabled="!canEdit" class="h-10 w-full" @click="importInspection" />
    </div>
    <div class="flex flex-col gap-3 w-full md:w-2/3">
      <div class="flex flex-col gap-1">
        <label class="font-bold">Inspection Date</label>
        <DatePicker
          v-model="inspectionDate"
          inputId="inspectionDate"
          showIcon
          icon-display="button"
          :show-on-focus="false"
          class="h-10"
          :disabled="!canEdit"
          inputClass="w-36"
          panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
          dateFormat="m/d/yy"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="font-bold">Instructions To Inspector</label>
        <Textarea
          class="w-full"
          :disabled="!canEdit"
          v-model="instructionsToInspector"
          placeholder="Enter any instructions for the inspector"
        ></Textarea>
      </div>
      <div class="flex flex-col gap-1">
        <label class="font-bold">Inspector Comments</label>
        <Textarea class="w-full" :disabled="!canEdit" v-model="inspectorComments" placeholder="Enter any comments from the inspector"></Textarea>
      </div>
    </div>
  </div>
  <div v-if="canEdit" class="flex justify-end mt-3"><SubmitButton type="button" @click="updateInspectionDetails" /></div>
  <TSDialog v-model:visible="showEmailComments" label="Inspection Request Comments" icon="pencil" size="h-fit w-[800px]!">
    <template #content>
      <p class="font-bold">Email Comments</p>
      <Textarea v-model="emailComments" auto-resize rows="10" class="w-full"></Textarea>
      <div class="flex justify-end mt-3">
        <SubmitButton label="Request Inspection" @click="requestInspection"></SubmitButton>
      </div>
    </template>
  </TSDialog>
  <TSDialog v-model:visible="showImportInspection" label="Import Inspection File" icon="import" size="h-fit w-[800px]!">
    <template #content>
      <ImportInspectionModal :draw-id="drawId" @import-complete="importComplete" />
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { toFormData } from "@/services/Utility";
import type { Project } from "@/types/Projects";
import type { ApiError } from "@/types/Response";
import InputNumber from "primevue/inputnumber";
import { useConfirm } from "primevue/useconfirm";

interface Props {
  project: Project;
  canEdit?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  canEdit: false,
});

const emit = defineEmits(["inspectionUpdated"]);

const confirm = useConfirm();
const emailComments = ref<string>("");
const showEmailComments = ref<boolean>(false);
const showImportInspection = ref<boolean>(false);

const drawId = props.project.projectDrawInfo.drawId;
const allocatedInspections = props.project.projectInfo.allocatedInspections;

const inspectionsThisDraw = ref<number>(props.project.projectDrawInfo.inspectionsThisDraw ? props.project.projectDrawInfo.inspectionsThisDraw : 0);
const instructionsToInspector = ref<string>(props.project.projectDrawInfo.instructionsToInspector);
const inspectorComments = ref<string>(props.project.projectDrawInfo.inspectorComments);
const inspectionDate = ref<Date | null>(props.project.projectDrawInfo.inspectionDate);

const inspectionsAvailable = computed<boolean>(() => {
  if (!allocatedInspections || allocatedInspections <= 0) return true;
  return allocatedInspections > inspectionsThisDraw.value;
});

const requestInspectionClick = (): void => {
  if (!inspectionsAvailable.value) {
    confirmPopup(
      confirm,
      "Confirm Inspection Request",
      "This project has reached its allocated number of inspections. Do you wish to continue with this inspection request?",
      () => (showEmailComments.value = true),
      () => info("Action cancelled", "Inspection request cancelled by user")
    );
  } else {
    showEmailComments.value = true;
  }
};

const importInspection = (): void => {
  if (!props.canEdit) return;
  showImportInspection.value = true;
};

const importComplete = (): void => {
  emit("inspectionUpdated");
  showImportInspection.value = false;
  success("Success", "Inspection file imported");
};

const requestInspection = (): void => {
  if (!props.canEdit) return;

  const data = toFormData({ comments: emailComments.value });

  axios
    .post(`/draw/${drawId}/inspection/request`, data)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          emailComments.value = "";
          showEmailComments.value = false;
          emit("inspectionUpdated");
          success("Success", "Inspection requested");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const updateInspectionDetails = (): void => {
  if (!props.canEdit) return;

  const data = {
    inspectionsThisDraw: inspectionsThisDraw.value,
    inspectionDate: inspectionDate.value,
    instructionsToInspector: instructionsToInspector.value,
    inspectorComments: inspectorComments.value,
  };

  axios
    .post(`/draw/${drawId}/inspection/update`, data)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          emit("inspectionUpdated");
          success("Success", "Inspection details updated");
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};
</script>
